import api, { deduplicatedApi } from "./index";

// ===== Contact Management =====

// Get all contacts with optional filters
// Using deduplicatedApi to prevent duplicate simultaneous requests
export const getContacts = (params = {}) =>
  deduplicatedApi.get("lead-management/contacts/", { params });

// Get a single contact by ID
export const getContact = (id) =>
  api.get(`lead-management/contacts/${id}/`);

// Create a new contact
export const createContact = (data) =>
  api.post("lead-management/contacts/", data);

// Update a contact (full update)
export const updateContact = (id, data) =>
  api.put(`lead-management/contacts/${id}/`, data);

// Partially update a contact
export const patchContact = (id, data) =>
  api.patch(`lead-management/contacts/${id}/`, data);

// Delete a contact
export const deleteContact = (id) =>
  api.delete(`lead-management/contacts/${id}/`);

// Admin-only: audit trail of profile changes for a contact
export const getContactChangelog = (id) =>
  api.get(`lead-management/contacts/${id}/changelog/`);

// Admin-only: list archived contacts (Trash)
export const getTrashedContacts = () =>
  api.get("lead-management/contacts/trash/");

// Admin-only: restore an archived contact from Trash
export const restoreContact = (id) =>
  api.post(`lead-management/contacts/${id}/restore/`);

// Admin-only: restore a whole group of archived contacts from Trash at once
export const bulkRestoreContacts = (ids) =>
  api.post("lead-management/contacts/restore-bulk/", { contact_ids: ids });

// Convert a contact to a lead
export const convertContactToLead = (id, data = {}) =>
  api.post(`lead-management/contacts/${id}/convert-to-lead/`, data);

// Sync a virtual contact (enquiry_<id> or buyer_<id>) to a CRM Contact; returns the contact so you can navigate
export const syncVirtualContact = (virtualId) =>
  api.post('lead-management/contacts/sync-virtual/', { virtual_id: virtualId });

// Send a buyer-portal invite (welcome email) to a contact without a portal account
export const inviteContactToPortal = (id) =>
  api.post(`lead-management/contacts/${id}/invite-to-portal/`);

// ===== Invite All: previewed, approved, rate-limited batches =====
// `params` are the Contacts page filters; `limit` caps the batch (e.g. a test send).

export const previewPortalInvites = (params = {}, limit) =>
  api.post('lead-management/contacts/portal-invites/preview/', { limit }, { params });

export const createPortalInviteBatch = (params = {}, { limit, note } = {}) =>
  api.post('lead-management/contacts/portal-invites/batches/', { limit, note }, { params });

export const getPortalInviteBatches = () =>
  api.get('lead-management/contacts/portal-invites/batches/');

export const getPortalInviteBatch = (id) =>
  api.get(`lead-management/contacts/portal-invites/batches/${id}/`);

export const getPortalInviteBatchRecipients = (id, params = {}) =>
  api.get(`lead-management/contacts/portal-invites/batches/${id}/recipients/`, { params });

// action: approve | reject | pause | resume | cancel
export const portalInviteBatchAction = (id, action) =>
  api.post(`lead-management/contacts/portal-invites/batches/${id}/${action}/`);


// ===== Buyer Requirements =====

// Get all buyer requirements with optional filters
export const getBuyerRequirements = (params = {}) =>
  api.get("lead-management/buyer-requirements/", { params });

// Get a single buyer requirement by ID
export const getBuyerRequirement = (id) =>
  api.get(`lead-management/buyer-requirements/${id}/`);

// Get buyer requirements for a specific lead
export const getBuyerRequirementsByLead = (leadId) =>
  api.get(`lead-management/buyer-requirements/lead/${leadId}/`);

// Create or update buyer requirements (upsert)
export const saveBuyerRequirements = (data) =>
  api.post("lead-management/buyer-requirements/", data);

// Update buyer requirements (full update)
export const updateBuyerRequirements = (id, data) =>
  api.put(`lead-management/buyer-requirements/${id}/`, data);

// Partially update buyer requirements
export const patchBuyerRequirements = (id, data) =>
  api.patch(`lead-management/buyer-requirements/${id}/`, data);

// Delete buyer requirements
export const deleteBuyerRequirements = (id) =>
  api.delete(`lead-management/buyer-requirements/${id}/`);

// ===== Contact Extensions (Notes, Reminders, Linked Properties) =====

// Notes, returns { notes: [...], last_contacted: {agent_name, date, is_yours}|null }.
// Agents only get their own notes back; last_contacted reflects the latest note by
// ANY agent (so they can see when the buyer was last spoken to without reading it).
export const getContactNotes = (id) =>
  api.get(`lead-management/contacts/${id}/notes/`);

export const addContactNote = (id, data) =>
  api.post(`lead-management/contacts/${id}/notes/`, data);

export const deleteContactNote = (id, noteId) =>
  api.delete(`lead-management/contacts/${id}/notes/${noteId}/`);

// Log a call ("Called") against a person on a property's Enquiries / Attendees /
// Buyers tab. Named by the RECORD the agent clicked - exactly ONE of attendeeId,
// enquiryId or buyerLinkId - so the backend resolves the contact and checks
// access from that record (never a client-supplied email/contact). `outcome` is
// the note text; `remindInDays` optionally sets a call-back reminder. Returns
// { note, last_contacted, contact_id }. 409 when the contact is Do Not Call.
export const markContactCalled = ({
  attendeeId, enquiryId, buyerLinkId, outcome, remindInDays,
} = {}) =>
  api.post('lead-management/contacts/mark-called/', {
    attendee_id: attendeeId,
    enquiry_id: enquiryId,
    buyer_link_id: buyerLinkId,
    outcome,
    remind_in_days: remindInDays,
  });

// Resolve a person shown in any list (portal buyer / analytics row) to a CRM
// contact and fetch their notes in one call. Pass any of {email, name, phone,
// buyerId, contactId}. Returns { contact_id, contact_name, notes }.
export const getNotesForIdentity = ({ email, name, phone, buyerId, contactId } = {}) =>
  api.post('lead-management/contacts/notes-for/', {
    email,
    name,
    phone,
    buyer_id: buyerId,
    contact_id: contactId,
  });

// Batch-fetch the most recent note per person for a list view (a property's
// Buyers / Attendees / Enquiries). Pass any mix of contact ids and emails.
// Returns { by_contact_id: {"<id>": {text, author, created_at}}, by_email: {...} }.
export const getLatestNotes = ({ contactIds = [], emails = [] } = {}) =>
  api.post('lead-management/contacts/latest-notes/', {
    contact_ids: contactIds,
    emails,
  });

// Resolve a person shown in any list (portal buyer / analytics / enquiry row) to
// a CRM contact id so we can navigate to their profile page. Pass any of
// {email, name, phone, buyerId, contactId}. Returns { contact_id, contact_name }.
// Get-or-creates the Contact server-side (same logic as getNotesForIdentity).
export const resolveContactIdentity = ({ email, name, phone, buyerId, contactId } = {}) =>
  api.post('lead-management/contacts/resolve/', {
    email,
    name,
    phone,
    buyer_id: buyerId,
    contact_id: contactId,
  });

// Reminders
export const getContactReminders = (id) =>
  api.get(`lead-management/contacts/${id}/reminders/`);

export const addContactReminder = (id, data) =>
  api.post(`lead-management/contacts/${id}/reminders/`, data);

export const updateContactReminder = (id, remId, data) =>
  api.patch(`lead-management/contacts/${id}/reminders/${remId}/`, data);

export const deleteContactReminder = (id, remId) =>
  api.delete(`lead-management/contacts/${id}/reminders/${remId}/`);

// Linked Properties
export const getContactProperties = (id) =>
  api.get(`lead-management/contacts/${id}/properties/`);

export const linkContactProperty = (id, data) =>
  api.post(`lead-management/contacts/${id}/properties/`, data);

export const unlinkContactProperty = (id, linkId) =>
  api.delete(`lead-management/contacts/${id}/properties/${linkId}/`);

// Contact Chat (Comms tab, buyer/agent two-way chat)
export const getContactMessages = (id) =>
  api.get(`lead-management/contacts/${id}/messages/`);

export const sendContactMessage = (id, body) =>
  api.post(`lead-management/contacts/${id}/messages/`, { body });

// Buyer Preferences Sync
export const syncContactBuyerPreferences = (id) =>
  api.post(`lead-management/contacts/${id}/sync-buyer-preferences/`);

// Get a contact's raw buying preferences in the buyer-portal shape (property_type,
// price_min, suburbs, investment_goals, …) plus portal-account status, used to
// pre-fill the agent-side editor that mirrors the portal's Buying Preferences form.
export const getContactBuyerPreferences = (id) =>
  api.get(`lead-management/contacts/${id}/buyer-preferences/`);

// Agent-side edit of a contact's buying preferences. Writes through to the portal
// BuyingPreference record (the source of truth for buyer-trend stats). Accepts the
// portal payload shape (property_type/buyer_type/finance_status/suburbs/
// investment_goals as arrays; price_*, bedrooms_*, etc. as strings). Returns the
// saved { preference } and the rebuilt { buyer_preferences } snapshot.
export const updateContactBuyerPreferences = (id, data) =>
  api.patch(`lead-management/contacts/${id}/buyer-preferences/`, data);

// Buying-preference changes read out of the agent's notes (Monday 13391227323).
// Send { text } to read one note, or nothing to read the buyer's saved notes.
// Never saves: returns { available, changes: [{field, label, current, suggested,
// value}], notes_read } and the agent applies with updateContactBuyerPreferences.
export const suggestBuyerPreferencesFromNotes = (id, data = {}) =>
  api.post(`lead-management/contacts/${id}/buyer-preferences/from-notes/`, data);

// Renter Preferences - the rental-side counterpart to the buyer preferences
// pair above, backed by a separate RentingPreference record so renter demand
// never mixes into buyer-preference analytics.
export const getContactRentingPreferences = (id) =>
  api.get(`lead-management/contacts/${id}/renting-preferences/`);

export const updateContactRentingPreferences = (id, data) =>
  api.patch(`lead-management/contacts/${id}/renting-preferences/`, data);


// Documents / Filing
export const getContactDocuments = (contactId) =>
  api.get("document-management/documents/", { params: { related_contact: contactId } });

export const uploadContactDocument = (contactId, file, name, category = "miscellaneous") => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", name);
  formData.append("related_contact", contactId);
  formData.append("category", category);
  return api.post("document-management/documents/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteContactDocument = (docId) =>
  api.delete(`document-management/documents/${docId}/`);

// Contact Enquiries & Offers (for contact profile tabs)
export const getContactEnquiries = (id) =>
  api.get(`lead-management/contacts/${id}/enquiries/`);

export const getContactOffers = (id) =>
  api.get(`lead-management/contacts/${id}/offers/`);

// Buyer Portal activity timeline for a contact (matched by email)
export const getContactBuyerActivity = (id) =>
  api.get(`lead-management/contacts/${id}/buyer-activity/`);

// Every email and text this contact was sent, automated ones included
// (Monday 13198163292). params: { channel: 'email'|'sms', limit, offset }
export const getContactCommsLog = (id, params = {}) =>
  api.get(`lead-management/contacts/${id}/comms-log/`, { params });

// One message from that list, as the contact received it.
export const getContactCommsMessage = (id, logId) =>
  api.get(`lead-management/contacts/${id}/comms-log/${logId}/message/`);

// Chatbot & messenger conversations for a contact
export const getContactChatSessions = (id) =>
  api.get(`lead-management/contacts/${id}/chat-sessions/`);

// Unified 360° analytics profile for a contact (property profile, searches, saves,
// suburbs, calculator usage, chat, portfolio, finance/advocacy/landlord status)
export const getContactProfileSummary = (id) =>
  api.get(`lead-management/contacts/${id}/profile-summary/`);


// Bulk add a group of buyers straight into the CRM, with one set of buying
// preferences and no Database row (Christian Gravias, Monday DOMA 12840979019).
// Note the path: lead_management is mounted WITHOUT the /api prefix.
export const bulkAddBuyers = (payload) =>
  api.post("lead-management/contacts/buyers/bulk-add/", payload);
